import { useCallback, useContext, useEffect, useRef } from 'react'
import { ReportContext } from '../contexts/ReportContext'
import ReactGA from 'react-ga4'
import { getMinutesSpentString } from '@utils/CommonHelper'
import rootStore from '../store/RootStore'
import { debounce } from 'lodash'

const ONE_SECOND = 1000
const SECTION_OFFSET = 300
const CHART_OFFSET = 300

function getMapOffsets(selector, offset) {
  const mappingOffsets = Array.from(document.querySelectorAll(selector))
  return mappingOffsets.map(element => ({
    min: element.offsetTop - offset,
    max: element.offsetTop + element.getBoundingClientRect().height - offset,
    id: element.dataset.reportSectionId || element.dataset.chartId,
  }))
}

function offsetChart() {
  return getMapOffsets('[data-chart-id]', CHART_OFFSET)
}

function offsetSection() {
  return getMapOffsets('[data-report-section-id]', SECTION_OFFSET)
}

export const findAllCurrentIds = (offsetArray, scrollTop, scrollBottom) =>
  offsetArray.filter(({ min, max }) => scrollTop < min && scrollBottom > max)


export const usePageScroll = () => {
  const {
    reportStore: {
      reportId,
      setCurrentSectionId,
    },
    chartStore: {
      fetchMultipleCharts,
      charts,
    },
  } = rootStore

  const timer = useRef(Date.now())
  const chartsRef = useRef(charts)
  const { refScrollContainer } = useContext(ReportContext)

  // Keep chartsRef in sync with charts without causing callback recreation
  useEffect(() => {
    chartsRef.current = charts
  }, [charts])

  const updateTimer = useCallback(() => {
    timer.current = Date.now()
  }, [])

  const findCurrentId = (offsetArray, distanceY) => {
    // Find all sections that contain the current scroll position
    const matchingSections = offsetArray.filter(({ min, max }) =>
      min < distanceY && max > distanceY
    )

    if (matchingSections.length === 0) return null

    matchingSections.sort((a, b) => b.min - a.min)

    return matchingSections[0]
  }

  const handleReportPageScroll = useCallback(() => {
    const handleScroll = () => {

      const distanceY = refScrollContainer.current?.scrollTop || 0
      const topY = distanceY - (window.innerHeight * 1.1)
      const bottomY = distanceY + (window.innerHeight * 1.1)

      const newChartsOffset = offsetChart()
      const currentCharts = findAllCurrentIds(newChartsOffset, topY, bottomY)

      const sectionOffset = offsetSection()
      const section = findCurrentId(sectionOffset, distanceY)
      if (section) {
        const deltaTime = Date.now() - timer.current
        setCurrentSectionId(section.id)
        if (deltaTime > ONE_SECOND) {
          ReactGA.event({
            category: 'section',
            action: 'section-change',
            label: `${section.id}: ${getMinutesSpentString(deltaTime)}`,
            value: parseInt(section.id),
          })
          updateTimer()
        }
      }

      // Batch fetch charts that aren't loaded yet
      // Limit batch size to prevent render spikes
      const MAX_CHARTS_PER_BATCH = 10
      if (currentCharts?.length > 0) {
        const missingChartIds = currentCharts
          .filter(chart => !chartsRef.current[chart.id])
          .map(chart => chart.id)
          .slice(0, MAX_CHARTS_PER_BATCH)

        if (missingChartIds.length > 0) {
          fetchMultipleCharts({ reportId, chartIds: missingChartIds })
        }
      }
    }

    const msToAccountAsANewScrollEvent = 100
    return debounce(handleScroll, msToAccountAsANewScrollEvent)
  }, [
    fetchMultipleCharts,
    reportId,
    refScrollContainer,
    setCurrentSectionId,
    updateTimer,
    // chartsRef intentionally omitted: refs are stable and don't trigger re-renders
    // Including it would defeat the performance optimization of using useRef
  ])

  const debouncedScrollHandler = handleReportPageScroll()

  useEffect(() => {
    const refScrollCurrent = refScrollContainer?.current
    if (refScrollCurrent) {

      refScrollCurrent.addEventListener('scroll', debouncedScrollHandler)
      setTimeout(() => {
        debouncedScrollHandler()
      }, 1000)
      return () => {
        refScrollCurrent.removeEventListener('scroll', debouncedScrollHandler)
      }
    }
  }, [debouncedScrollHandler, refScrollContainer])
}

export default usePageScroll
